import React, {
  FC, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
// eslint-disable-next-line
import '@google/model-viewer';
// eslint-disable-next-line
import { ModelViewerElement } from '@google/model-viewer';
import './ARViewer.scss';
import { HotspotButton } from './components/HotspotButton';
import { ARViewerModel } from './model/ARViewerModel';
import { SvgComponent, CloseBtn } from './components/SvgComponent';
import { Popup } from './components/PopUp';
import { PopupIOs } from './components/PopUpIOs';
import { track } from '../../tracking';

type ARViewerProps = {
  model: ARViewerModel,
  lang: string,
  animal: string
}

type btnCopy = {
  [key: string]: string
};

type infoCopy = {
  [key: string]: string
};

const isMobile = /Android|webOS|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
const isChromeIOs = /CriOS/i.test(navigator.userAgent);

export const ARViewer: FC<ARViewerProps> = ({ model: modelData, animal, lang }) => {
  const modelViewerRef = useRef<ModelViewerElement>();
  const soundArray:Array<HTMLAudioElement> = [];
  const [selectedHotspot, setSelectedHotspot] = useState(-1);
  const [runOnce, setRunOnce] = useState(true);
  const [showHotSpots, setShowHotSpots] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [showPopupIOs, setShowPopupIOs] = useState(false);
  const [isLoaded, setLoaded] = useState(false);
  const [arErrorSeen, setArErrorSeen] = useState(false);
  const showArError = useMemo(() => !arErrorSeen && !runOnce, [arErrorSeen, runOnce]);

  const wait = (ms:number) => new Promise((resolve) => setTimeout(resolve, ms));

  const arBTNCopy: btnCopy = {
    en: 'VIEW THE SURROUNDINGS',
    de: 'IN UMGEBUNG ANSEHEN',
    pl: 'ZOBACZ OKOLICE',
  };

  const arInfoCopy: infoCopy = {
    en: 'Augmented Reality is not supported by your device. Enjoy our application on an ARCore-compatible device. Thank you for your understanding!',
    de: 'Augmented Reality wird von Ihrem Smartphone nicht unterstützt. Genießen Sie unsere Anwendung mit einem ARCore-kompatiblen Gerät. Vielen Dank für Ihr Verständnis!',
    pl: 'Twój smartfon nie obsługuje rozszerzonej rzeczywistości. Korzystaj z naszej aplikacji na urządzeniu kompatybilnym z ARCore. Dziękujemy za zrozumienie!',
  };

  const animateTheModel = async () => {
    if (modelViewerRef && modelViewerRef.current) {
      modelViewerRef.current.src = modelData.glbSrc;
      modelViewerRef.current.animationName = modelData.animations[0].name;

      const intervalLength = parseInt(modelData.animations[0].duration, 10);

      await wait(intervalLength);

      if (modelViewerRef && modelViewerRef.current) {
        modelViewerRef.current.animationName = modelData.animations[1].name;
        setShowHotSpots(true);
      }
    }
  };

  const stopSounds = useCallback(() => {
    if (soundArray.length > 0) {
      soundArray.forEach((element:HTMLAudioElement) => {
        element.pause();
      });
    }
  }, []);

  const playSound = useCallback((sound) => {
    const audio = new Audio(sound);

    stopSounds();

    if (lang) {
      audio.play();
    }

    soundArray.push(audio);

    track({
      event: 'PlaySound',
      sound,
      animal,
      lang,
      value: animal,
    });
  }, []);

  const handlePopup = () => {
    setShowPopup(false);
    setShowPopupIOs(false);
  };

  const handleHotspotClick = useCallback((hotspotIndex, hotspotElement, appLang) => {
    setSelectedHotspot(hotspotIndex);

    if (!runOnce) {
      setArErrorSeen(true);
    }

    if (hotspotIndex > -1 && hotspotElement) {
      playSound(hotspotElement.lang[appLang].audio);
      track({
        event: 'ClickHotspot',
        animal,
        lang,
        value: animal,
      });
    }
  }, [playSound, runOnce]);

  const handleRootClick = useCallback((event) => {
    if (selectedHotspot > -1 && !event.target.classList.contains('container-overlay') && !event.target.classList.contains('hotspot-button')) {
      stopSounds();
      handleHotspotClick(-1, null, lang);
    }
  }, [selectedHotspot, handleHotspotClick]);

  const openAR = (event: any) => {
    event.preventDefault();

    track({
      event: 'ViewInAR',
      animal,
      lang,
      value: animal,
    });
  };

  useEffect(() => {
    if (isMobile) {
      setTimeout(() => setShowPopup(true), 4000);
    }

    track({
      event: isMobile ? 'ViewOnMobile' : 'ViewOnDesktop',
      animal,
      lang,
      value: animal,
    });

    if (isChromeIOs) {
      setTimeout(() => {
        setShowPopup(false);
        setShowPopupIOs(true);
      }, 4000);
    }

    modelViewerRef.current?.addEventListener('load', (event: any) => {
      setLoaded(true);
    });

    modelViewerRef.current?.addEventListener('ar-status', (event: any) => {
      if (runOnce) {
        setRunOnce(false);
      }

      if (modelData.animations.length > 0) {
        const arStatusText = event.detail.status;

        if (modelViewerRef.current) {
          if (arStatusText === '') {
            modelViewerRef.current.animationName = modelData.animations[1].name;
          }

          if (arStatusText === 'session-started' && runOnce) {
            setShowHotSpots(false);
            animateTheModel();
          }
        }
      }
    });
  }, []);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="ARViewer" onClick={handleRootClick}>
      {showPopup && <Popup lang={lang} handlePopup={handlePopup} /> }

      {showPopupIOs && <PopupIOs lang={lang} handlePopup={handlePopup} /> }

      <model-viewer
        ref={modelViewerRef}
        src={modelData.glbSrc}
        ios-src={modelData.usdzSrc}
        exposure={modelData.shadowIntensity}
        ar
        ar-modes="scene-viewer webxr quick-look"
        camera-controls
        poster={modelData.poster}
        shadow-intensity="1"
        autoplay
        animation-name="idle"
        camera-orbit={modelData.cameraOrbit}
        camera-target={modelData.cameraTarget}
        field-of-view="30deg"
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: modelData.posterBg,
        }}
        disable-tap
        disable-pan
      >
        {showHotSpots && (
          modelData.hotspots.map((hotspot, index) => (
            <HotspotButton
              key={`button_${hotspot.slot}`}
              className="hotspot-button"
              onClick={() => handleHotspotClick(index, hotspot, lang)}
              slot={hotspot.slot}
              data-position={hotspot.dataPosition}
              data-normal={hotspot.dataNormal}
            />
          ))
        )}

        <div className="progress-bar hide" slot="progress-bar">
          <div className="update-bar" />
        </div>

        <button
          type="button"
          className="ar-button"
          slot="ar-button"
          id="ar-button"
          onClick={openAR}
          style={{
            display: isLoaded ? 'block' : 'none',
          }}
        >
          <SvgComponent model={modelData} />

          {arBTNCopy[lang]}
        </button>

        {showArError && (
          <div
            className="ar-button ar-failed ar-info"
            id="ar-button"
          >
            {arInfoCopy[lang]}
          </div>
        )}

        {modelData.hotspots.map((hotspot, index) => (
          <div key={`${hotspot.slot}`} className="container-overlay" style={{ visibility: selectedHotspot === index ? 'visible' : 'hidden' }}>
            <div className="close-container">
              <CloseBtn model={modelData} />
            </div>
            <h2 className="overlay-title">{hotspot.lang[lang].title}</h2>
            <p className="overlay-paragraph">
              {hotspot.lang[lang].text}
            </p>
          </div>
        ))}
      </model-viewer>
    </div>
  );
};
